import { Formik } from 'formik';
import React, { useState } from 'react';

import Text from '../Text';
import { TextArea } from '../TextArea/TextArea';
import FeedbackItem from './FeedbackItem';
import ThankYou from './ThankYou';
import { CompletionSurveyProps, ComponentProps, SelectionTypes } from './interfaces';
import { FeedbackWrapper, IconsWrapper, ButtonWrapper, Spacer, ButtonContainer } from './styled';

export const CompletionSurvey = React.forwardRef<HTMLFormElement, CompletionSurveyProps>(
  function CompletionSurvey(
    {
      onThumbSelect,
      onSkip,
      allowTextFeedback = true,
      isGivenFeedback,
      feedbackType,
      isSent,
      locale: {
        completionText = 'Thank you',
        prompt = 'How was your experience?',
        thumbsUpLabel = 'Good',
        thumbsDownLabel = 'Bad',
        textAreaLabelGood = 'Thank you, please tell us why (optional)',
        textAreaLabelBad = 'How can we improve? (optional)',
        textAreaPlaceholder = "Don't include any confidential or personal info in your feedback. (No names, phone numbers or emails.)",
        submitActionLabel = 'Send feedback',
        skipActionLabel = 'Skip',
        privacyLinkLabel = 'Privacy statement'
      } = {},
      onSubmit,
      disableFeedbackChange = false,
      feedbackTextAreaProps,
      thumbsUpProps,
      thumbsDownProps,
      submitFeedbackProps,
      skipFeedbackProps,
      privacyLinkProps
    },
    ref
  ) {
    const spacerRef = React.useRef<HTMLDivElement>(null);

    const handleClick = (type: SelectionTypes) => {
      if (!(isGivenFeedback && disableFeedbackChange)) {
        if (onThumbSelect) {
          onThumbSelect(type);
        }

        // scroll the input form into view 10ms delay
        setTimeout(() => {
          spacerRef?.current?.scrollIntoView({
            behavior: 'smooth'
          });
        }, 10);
      }
    };

    return isSent ? (
      <ThankYou text={completionText} />
    ) : (
      <form onSubmit={onSubmit} action='#' ref={ref}>
        <Text.Heading.H3 fontWeight={600} style={{ marginBottom: '15px' }}>
          {prompt}
        </Text.Heading.H3>
        <FeedbackWrapper>
          <IconsWrapper>
            <FeedbackItem
              type='up'
              onClick={() => handleClick('up')}
              disabled={!!isGivenFeedback && feedbackType !== 'up'}
              selected={feedbackType === 'up'}
              feedbackText={thumbsUpLabel}
              data-testid='feedback-thumbs-up'
              {...thumbsUpProps}
            />
            <FeedbackItem
              type='down'
              onClick={() => handleClick('down')}
              disabled={!!isGivenFeedback && feedbackType !== 'down'}
              selected={feedbackType === 'down'}
              feedbackText={thumbsDownLabel}
              data-testid='feedback-thumbs-down'
              {...thumbsDownProps}
            />
          </IconsWrapper>
          {isGivenFeedback && !allowTextFeedback && (
            <>
              <Spacer space={16} />
              <ThankYou text={completionText} />
            </>
          )}
        </FeedbackWrapper>
        {isGivenFeedback && allowTextFeedback && (
          <>
            <Spacer space={30} />
            <TextArea
              label={feedbackType === 'up' ? textAreaLabelGood : textAreaLabelBad}
              placeholder={textAreaPlaceholder}
              maxLength={300}
              {...feedbackTextAreaProps}
            />
            <Text.Link
              href='https://service.vic.gov.au/privacy-and-security'
              data-testid='feedback-privacy-link'
              {...privacyLinkProps}>
              {privacyLinkLabel}
            </Text.Link>
            <Spacer space={24} ref={spacerRef} />
            <ButtonContainer>
              <ButtonWrapper
                buttonType='primary'
                type='submit'
                data-testid='feedback-send'
                {...submitFeedbackProps}>
                {submitActionLabel}
              </ButtonWrapper>
              <ButtonWrapper
                buttonType='secondary'
                data-testid='feedback-skip'
                {...skipFeedbackProps}
                onClick={e => {
                  if (onSkip && feedbackType) {
                    onSkip(feedbackType);
                  }
                  skipFeedbackProps?.onClick?.(e);
                }}>
                {skipActionLabel}
              </ButtonWrapper>
            </ButtonContainer>
          </>
        )}
      </form>
    );
  }
);

const CompletionSurveyWithFormik = React.forwardRef<HTMLFormElement, ComponentProps>(
  function CompletionSurveyWithFormik(
    { onFeedbackGiven, form, onThumbSelect, onSkip, ...props },
    ref
  ) {
    const [feedback, setFeedback] = useState<{ isGivenFeedback: boolean; type?: SelectionTypes }>({
      isGivenFeedback: false
    });
    const [sent, setSent] = useState(false);

    return (
      <Formik
        initialValues={{ feedback: '' }}
        onSubmit={values => {
          setSent(true);
          if (onFeedbackGiven && feedback.type) {
            onFeedbackGiven(feedback.type, values.feedback);
          }
        }}
        {...form}>
        {({ handleSubmit, handleChange, handleBlur }) => (
          <CompletionSurvey
            ref={ref}
            onSubmit={handleSubmit}
            onThumbSelect={type => {
              setFeedback({
                isGivenFeedback: true,
                type
              });

              onThumbSelect?.(type);
            }}
            onSkip={type => {
              setSent(true);
              onSkip?.(type);
            }}
            feedbackType={feedback.type}
            isGivenFeedback={feedback.isGivenFeedback}
            isSent={sent}
            feedbackTextAreaProps={{
              name: 'feedback',
              onChange: handleChange,
              onBlur: handleBlur
            }}
            {...props}
          />
        )}
      </Formik>
    );
  }
);

export default CompletionSurveyWithFormik;
